import {
  SidebarGroupContent,
  SidebarMenuButton,
  SidebarMenuBadge,
  SidebarSeparator,
  SidebarMenuItem,
  SidebarContent,
  TooltipContent,
  TooltipTrigger,
  SidebarFooter,
  SidebarHeader,
  SidebarGroup,
  SidebarMenu,
  useSidebar,
  Sidebar,
  Counter,
  Tooltip,
  Logo,
} from '@ui/components';
import { NavChevronRight, NavChevronLeft, Bell } from '@ui/components/icons';
import { useLocation, NavLink } from 'react-router-dom';
import { useCallback, useEffect, FC } from 'react';
import { HasPermission } from '@shared/components';
import { useTranslation } from 'react-i18next';
import { ssManager } from '@shared/utils';
import { useMediaQuery } from '@ui/hooks';
import { Routes } from '@shared/enums';
import { cn } from '@ui/utils';

import { useNotificationsCount } from './use-notifications-count';
import { UserSettings } from '../user-settings/user-settings';
import { MENU_ROUTES } from './navbar.routes';

export const Navbar: FC = () => {
  const { open, toggleSidebar, setOpen } = useSidebar();
  const isDesktopSmall = useMediaQuery('DESKTOP_SMALL');
  const isMobile = useMediaQuery('MOBILE');
  const { t } = useTranslation();
  const location = useLocation();

  const notificationsCount = useNotificationsCount();

  useEffect(() => {
    ssManager.set('SIDEBAR_OPEN', open.toString());
  }, [open]);

  useEffect(() => {
    if (isDesktopSmall) setOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDesktopSmall]);

  const isRouteActive = useCallback(
    (route: Routes) => location.pathname.includes(route),
    [location.pathname],
  );

  return (
    <>
      <Sidebar collapsible="icon" variant="floating">
        <SidebarHeader className="overflow-hidden">
          <NavLink to={Routes.DASHBOARD}>
            <Logo className={cn('block', { 'mx-auto': !open })} short={!open} size="small" />
          </NavLink>
        </SidebarHeader>

        <SidebarContent>
          <SidebarGroup>
            <SidebarGroupContent>
              <SidebarMenu>
                {MENU_ROUTES.map((item) => (
                  <HasPermission
                    permissionFeature={item.permission}
                    permissionType="READ"
                    key={item.title}
                  >
                    <SidebarMenuItem>
                      <Tooltip disableHoverableContent>
                        <TooltipTrigger asChild>
                          <SidebarMenuButton isActive={isRouteActive(item.url)} asChild>
                            <NavLink to={item.url}>
                              <item.icon />
                              <span>{t(item.title)}</span>
                            </NavLink>
                          </SidebarMenuButton>
                        </TooltipTrigger>
                        {!open && (
                          <TooltipContent className="w-fit" sideOffset={10} side="bottom">
                            {t(item.title)}
                          </TooltipContent>
                        )}
                      </Tooltip>
                    </SidebarMenuItem>
                  </HasPermission>
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        </SidebarContent>

        <SidebarFooter className="relative">
          {!isMobile && (
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  className="absolute -top-8 -right-1.5 bg-background py-1 px-px rounded cursor-pointer"
                  onClick={toggleSidebar}
                >
                  {open ? (
                    <NavChevronLeft className="size-4" />
                  ) : (
                    <NavChevronRight className="size-4" />
                  )}
                </button>
              </TooltipTrigger>
              <TooltipContent className="w-fit">
                {t(open ? 'NAVBAR.COLLAPSE_MENU' : 'NAVBAR.EXPAND_MENU')}
              </TooltipContent>
            </Tooltip>
          )}

          <HasPermission permissionFeature="NOTIFICATIONS" permissionType="READ">
            <SidebarMenu>
              <SidebarMenuItem>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <SidebarMenuButton isActive={isRouteActive(Routes.NOTIFICATIONS)} asChild>
                      <NavLink to={Routes.NOTIFICATIONS} className="relative">
                        <Bell />
                        <span>{t('NAVBAR.NOTIFICATIONS')}</span>

                        {!open && (
                          <>
                            {!!notificationsCount && (
                              <Counter
                                className="absolute translate-x-1/2 -translate-y-1/2"
                                size="small"
                              >
                                {notificationsCount}
                              </Counter>
                            )}
                            <TooltipContent className="w-fit" sideOffset={10} side="bottom">
                              {t('NAVBAR.NOTIFICATIONS')}
                            </TooltipContent>
                          </>
                        )}
                      </NavLink>
                    </SidebarMenuButton>
                  </TooltipTrigger>
                </Tooltip>

                {!!notificationsCount && (
                  <SidebarMenuBadge>
                    <Counter>{notificationsCount}</Counter>
                  </SidebarMenuBadge>
                )}
              </SidebarMenuItem>
            </SidebarMenu>
          </HasPermission>

          <SidebarSeparator />
          <UserSettings sidebarOpen={open} />
        </SidebarFooter>
      </Sidebar>
    </>
  );
};
